// DEPRECATED: prefer URL path/query params for student-data state. The active student
// and period already live in the path; the timeframe / include / format selections here
// should migrate to query params so they're shareable and survive refresh. New state for
// this feature should not be added to this store.
import { create } from 'zustand';

import { type Timeframe, type MessageFormat } from '~/services/studentData';

export type IncludeOptions = { praises: boolean; warnings: boolean; attendance: boolean };

const DEFAULT_TIMEFRAME: Timeframe = '14d';
const DEFAULT_INCLUDE: IncludeOptions = { praises: true, warnings: true, attendance: true };
const DEFAULT_FORMAT: MessageFormat = 'summary';

interface StudentDataStore {
  // ─── Shared state (synchronized across all tabs) ──────────────────────
  timeframe: Timeframe;

  setTimeframe: (timeframe: Timeframe) => void;

  // ─── Custom Messages — draft selections ───────────────────────────────
  // What's shown in the form panel. Persists across tab/student switches.
  // Committed to `applied*` (and to shared `timeframe`) when the user clicks Go!.
  draftInclude: IncludeOptions;
  draftFormat: MessageFormat;
  draftTimeframe: Timeframe;

  setDraftInclude: (include: IncludeOptions) => void;
  setDraftFormat: (format: MessageFormat) => void;
  setDraftTimeframe: (timeframe: Timeframe) => void;

  // ─── Custom Messages — applied selections ─────────────────────────────
  // Drive the message recap label and the currently-displayed message.
  // Applied timeframe is the shared `timeframe` above (no separate field).
  appliedInclude: IncludeOptions;
  appliedFormat: MessageFormat;

  setAppliedInclude: (include: IncludeOptions) => void;
  setAppliedFormat: (format: MessageFormat) => void;
}

export const studentDataStore = create<StudentDataStore>()((set) => ({
  timeframe: DEFAULT_TIMEFRAME,

  // Also updates draftTimeframe so external changes (e.g. from Overview) flow into the Custom
  // Messages dropdown. User-driven dropdown edits in Custom Messages go through setDraftTimeframe
  // and stay in the draft until Go!.
  setTimeframe: (timeframe) => set({ timeframe, draftTimeframe: timeframe }),

  draftInclude: DEFAULT_INCLUDE,
  draftFormat: DEFAULT_FORMAT,
  draftTimeframe: DEFAULT_TIMEFRAME,

  setDraftInclude: (draftInclude) => set({ draftInclude }),
  setDraftFormat: (draftFormat) => set({ draftFormat }),
  setDraftTimeframe: (draftTimeframe) => set({ draftTimeframe }),

  appliedInclude: DEFAULT_INCLUDE,
  appliedFormat: DEFAULT_FORMAT,

  // Also updates draft counterparts so external changes (e.g. Data Log filter toggles, or
  // Custom Messages's Go!) flow into the Custom Messages form. Single source of truth: when
  // applied state changes, the draft mirrors it.
  setAppliedInclude: (appliedInclude) => set({ appliedInclude, draftInclude: appliedInclude }),
  setAppliedFormat: (appliedFormat) => set({ appliedFormat, draftFormat: appliedFormat }),
}));
